import React from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import { Car } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
    getMainLayoutNavLinkStyles,
    mainLayoutStyles,
} from './MainLayout.styles';

const MainLayout: React.FC = () => {
    const { t } = useTranslation();
    const { isAuthenticated, logout } = useAuth();
    const location = useLocation();

    const navLinks = [
        { label: t('nav.home'), href: '/' },
        { label: t('nav.about'), href: '/about' },
        { label: t('nav.howItWorks'), href: '/how-it-works' },
        { label: t('nav.support'), href: '/support' },
    ];

    const footerLinks = [
        { label: t('nav.about'), href: '/about' },
        { label: t('nav.howItWorks'), href: '/how-it-works' },
        { label: t('nav.support'), href: '/support' },
        { label: t('nav.register'), href: '/register' },
    ];

    return (
        <Box sx={mainLayoutStyles.root}>
            <AppBar position="sticky" sx={mainLayoutStyles.appBar}>
                <Container maxWidth="xl">
                    <Toolbar disableGutters sx={mainLayoutStyles.toolbar}>
                        {/* Logo */}
                        <Box component={Link} to="/" sx={mainLayoutStyles.logoLink}>
                            <Box sx={mainLayoutStyles.logoIcon}>
                                <Car size={20} color="white" />
                            </Box>
                            <Typography variant="h6" fontWeight={700} color="primary.main" letterSpacing="-0.5px">
                                {t('common.appName')}
                            </Typography>
                        </Box>

                        {/* Nav */}
                        <Box sx={mainLayoutStyles.desktopNav}>
                            {navLinks.map((link) => (
                                <Button
                                    key={link.href}
                                    component={Link}
                                    to={link.href}
                                    sx={getMainLayoutNavLinkStyles(location.pathname === link.href)}
                                >
                                    {link.label}
                                </Button>
                            ))}
                        </Box>

                        {/* Auth buttons */}
                        <Box sx={mainLayoutStyles.authActions}>
                            {isAuthenticated ? (
                                <>
                                    <Button component={Link} to="/app" color="inherit" size="small">
                                        {t('nav.goToApp')}
                                    </Button>
                                    <Button color="error" size="small" onClick={() => logout()}>
                                        {t('nav.logout')}
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Button component={Link} to="/login" color="inherit" size="small">
                                        {t('nav.login')}
                                    </Button>
                                    <Button component={Link} to="/register" variant="contained" size="small">
                                        {t('nav.register')}
                                    </Button>
                                </>
                            )}
                        </Box>
                    </Toolbar>
                </Container>
            </AppBar>

            <Box component="main" sx={mainLayoutStyles.mainContent}>
                <Outlet />
            </Box>

            {/* Footer */}
            <Box component="footer" sx={mainLayoutStyles.footer}>
                <Container maxWidth="xl">
                    <Box sx={mainLayoutStyles.footerGrid}>
                        <Box>
                            <Box sx={mainLayoutStyles.footerBrand}>
                                <Box sx={mainLayoutStyles.footerBrandIcon}>
                                    <Car size={20} color="white" />
                                </Box>
                                <Typography variant="h6" fontWeight={700}>{t('common.appName')}</Typography>
                            </Box>
                            <Typography variant="body2" color="grey.400" maxWidth={400}>
                                {t('mainLayout.footerDescription')}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="overline" color="grey.300" display="block" mb={1.5}>{t('mainLayout.footerQuickLinks')}</Typography>
                            {footerLinks.map((item) => (
                                <Typography key={item.href} variant="body2" color="grey.400" mb={1}>
                                    <Box component={Link} to={item.href} sx={mainLayoutStyles.footerLink}>
                                        {item.label}
                                    </Box>
                                </Typography>
                            ))}
                        </Box>
                        <Box>
                            <Typography variant="overline" color="grey.300" display="block" mb={1.5}>{t('mainLayout.footerContact')}</Typography>
                            <Typography variant="body2" color="grey.400">{t('mainLayout.footerContactEmail')}</Typography>
                            <Typography variant="body2" color="grey.400">{t('mainLayout.footerContactPhone')}</Typography>
                            <Typography variant="body2" color="grey.400">{t('mainLayout.footerContactHours')}</Typography>
                        </Box>
                    </Box>
                    <Box sx={mainLayoutStyles.footerBottom}>
                        <Typography variant="body2" color="grey.500">
                            {t('mainLayout.footerCopyright', { year: new Date().getFullYear() })}
                        </Typography>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default MainLayout;
